import {
  type CommunicationPreferencesDtoWritable,
  type CommunicationPreferencesSettingsDto,
  zCommunicationPreferencesDtoWritable,
} from '@platform/api';
import { CATEGORIES, type NotificationChannel } from './preference-matrix.constants';

export type FormValues = Record<string, Record<string, boolean>>;

export function settingsToFormValues(settings: CommunicationPreferencesSettingsDto, channels: readonly NotificationChannel[]): FormValues {
  return Object.fromEntries(
    Object.entries(settings).map(([key, pref]) => [
      key,
      Object.fromEntries(channels.map((ch) => [ch, (pref.activeChannels ?? []).includes(ch)])),
    ]),
  );
}

export function formValuesToDto(values: FormValues, settings: CommunicationPreferencesSettingsDto): CommunicationPreferencesDtoWritable {
  return zCommunicationPreferencesDtoWritable.parse({
    settings: Object.fromEntries(
      Object.entries(settings).map(([key, pref]) => {
        const channels = values[key] ?? {};
        const enabled = Object.entries(channels)
          .filter(([, isEnabled]) => isEnabled)
          .map(([ch]) => ch);
        const hidden = (pref.activeChannels ?? []).filter((ch) => !(ch in channels));
        return [key, { activeChannels: [...enabled, ...hidden] }];
      }),
    ),
  });
}

export type ToggleDiff = {
  toggled: Array<{ channel: NotificationChannel; category: keyof CommunicationPreferencesSettingsDto; enabled: boolean }>;
  masterToggled: Array<{ channel: NotificationChannel; enabled: boolean }>;
};

/**
 * Diffs the form's before/after values for one save. A channel whose categories all move to the
 * same value (fully on or fully off) is reported once as `masterToggled`, not as individual
 * `toggled` entries — covers both a single channel and every channel flipping uniformly.
 */
export function diffToggleEvents(before: FormValues, after: FormValues, channels: readonly NotificationChannel[]): ToggleDiff {
  const changesByChannel = channels.map((channel) => {
    const items = CATEGORIES.map((cat) => ({
      category: cat.id,
      before: before[cat.id]?.[channel] ?? false,
      after: after[cat.id]?.[channel] ?? false,
    }));
    const changed = items.filter((item) => item.before !== item.after);
    const uniform = changed.length === CATEGORIES.length && new Set(changed.map((item) => item.after)).size === 1;
    return { channel, changed, uniform };
  });

  const masterToggled = changesByChannel
    .filter((c) => c.uniform)
    .map((c) => ({ channel: c.channel, enabled: c.changed[0]?.after ?? false }));

  const toggled = changesByChannel
    .filter((c) => !c.uniform)
    .flatMap((c) => c.changed.map((item) => ({ channel: c.channel, category: item.category, enabled: item.after })));

  return { toggled, masterToggled };
}
